if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.form.addEventListener('change', this.clearSizeSelectionErrorState.bind(this));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.variantIdInput?.value) {
          this.showSizeSelectionErrorState();
          return;
        }

        this.error = false;
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const requestedQty = parseInt(formData.get('quantity') || '1', 10);
        const selectedVariantId = parseInt(formData.get('id'), 10);
        this.ensureColorCodeOnFormData(formData, selectedVariantId);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        this.enforceAvailabilityCutoff(selectedVariantId, requestedQty)
          .then((availability) => {
            // null / skipStockCheck → χωρίς έλεγχο αποθέματος (continue selling, όχι tracking, ή αποτυχία API)
            if (!availability || availability.skipStockCheck) {
              config.body = formData;
              return fetch(`${routes.cart_add_url}`, config);
            }

            if (availability.availableToAdd <= 0) {
              this.handleErrorMessage('This size is out of stock.');
              this.error = true;
              return null;
            }

            const finalQty = Math.min(requestedQty, availability.availableToAdd);
            if (finalQty !== requestedQty) {
              formData.set('quantity', String(finalQty));
              const qtyInput = this.form.querySelector('input[name="quantity"]');
              if (qtyInput) qtyInput.value = String(finalQty);
              this.handleErrorMessage(
                `Requested ${requestedQty}, but only ${finalQty} available. Quantity adjusted automatically.`
              );
            }

            config.body = formData;
            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => (response ? response.json() : null))
          .then(async (response) => {
            if (!response) return;

            // Μόνο απαντήσεις σφάλματος του cart/add (π.χ. 422)· όχι truthy "status" τύπου 200 που κάποια endpoints δίνουν σε επιτυχία
            const addStatus = Number(response.status);
            if (Number.isFinite(addStatus) && addStatus >= 400) {
              const partialAddResponse = await this.tryPartialAddOnStockError(response, formData);
              if (partialAddResponse) {
                this.handleErrorMessage(
                  `Requested ${partialAddResponse._requestedQty}, but only ${partialAddResponse._partialQty} available. Added available quantity to cart.`
                );
                this.handleSuccessfulAdd(partialAddResponse, formData);
                return;
              }

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              this.error = true;
              return;
            }

            this.handleSuccessfulAdd(response, formData);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async enforceAvailabilityCutoff(variantId, requestedQty) {
        if (!variantId || requestedQty <= 0) return { skipStockCheck: true };

        try {
          const root = window.Shopify?.routes?.root || '/';
          const [variantRes, cartRes] = await Promise.all([
            fetch(`${root}variants/${variantId}.js`),
            fetch(`${root}cart.js`),
          ]);
          if (!variantRes.ok || !cartRes.ok) return { skipStockCheck: true };

          const variant = await variantRes.json();
          const cart = await cartRes.json();

          if (variant.inventory_management !== 'shopify' || variant.inventory_policy === 'continue') {
            return { skipStockCheck: true };
          }

          // Αν το storefront δεν εκθέτει ποσότητα (πολυτοποθέτηση κ.λπ.), μην μπλοκάρεις· το Shopify επιβάλλει server-side
          const rawInv = variant.inventory_quantity;
          if (rawInv === null || rawInv === undefined) {
            return { skipStockCheck: true };
          }
          const invQty = parseInt(rawInv, 10);
          if (!Number.isFinite(invQty)) {
            return { skipStockCheck: true };
          }

          const inCartQty =
            cart.items?.reduce((sum, item) => {
              const lineVariantId =
                item.variant_id != null ? Number(item.variant_id) : Number(item.id);
              return lineVariantId === variantId ? sum + item.quantity : sum;
            }, 0) || 0;
          const availableToAdd = Math.max(0, invQty - inCartQty);
          return { availableToAdd };
        } catch (e) {
          console.error(e);
          return { skipStockCheck: true };
        }
      }

      showSizeSelectionErrorState() {
        const productInfo = this.closest('product-info');
        const sizeFieldset = productInfo?.querySelector('fieldset.option-size');
        const hasSizeOption = !!sizeFieldset;
        this.toggleSubmitButton(true, hasSizeOption ? window.variantStrings.selectSize : null);

        const sizeGrid = sizeFieldset?.querySelector('.size-options-grid');
        const selectedValueNode = sizeFieldset?.querySelector('span[data-selected-value]');

        sizeGrid?.classList.add('size-selection-error');
        if (selectedValueNode && hasSizeOption) {
          selectedValueNode.innerHTML = `<span class="size-selection-error-text">${window.variantStrings.selectSizePrompt}</span>`;
        }

        if (sizeFieldset) {
          const headerHeight =
            parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
          const top = sizeFieldset.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

          const firstSizeInput = sizeFieldset.querySelector('input[type="radio"]:not(:disabled)');
          if (firstSizeInput) {
            window.setTimeout(() => firstSizeInput.focus({ preventScroll: true }), 400);
          }
        }
      }

      clearSizeSelectionErrorState() {
        const productInfo = this.closest('product-info');
        const sizeFieldset = productInfo?.querySelector('fieldset.option-size');
        const sizeGrid = sizeFieldset?.querySelector('.size-options-grid');
        const selectedValueNode = sizeFieldset?.querySelector('span[data-selected-value]');
        const checkedSizeInput = sizeFieldset?.querySelector('input[type="radio"]:checked');

        sizeGrid?.classList.remove('size-selection-error');

        if (checkedSizeInput) {
          this.toggleSubmitButton(false);
          if (selectedValueNode) selectedValueNode.textContent = checkedSizeInput.value;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      handleSuccessfulAdd(response, formData) {
        this.error = false;

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          cartData: response,
        }).then(() => {
          CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
        });
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure('add:paint-updated-sections', () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.renderContents(response);
          });
        }
      }

      async tryPartialAddOnStockError(response, formData) {
        if (parseInt(response.status, 10) !== 422) return null;

        const requestedQty = parseInt(formData.get('quantity') || '1', 10);
        const variantId = parseInt(formData.get('id'), 10);
        if (!variantId || requestedQty <= 1) return null;

        try {
          const root = window.Shopify?.routes?.root || '/';
          const [variantRes, cartRes] = await Promise.all([
            fetch(`${root}variants/${variantId}.js`),
            fetch(`${root}cart.js`),
          ]);
          if (!variantRes.ok || !cartRes.ok) return null;

          const variant = await variantRes.json();
          const cart = await cartRes.json();

          if (variant.inventory_management !== 'shopify' || variant.inventory_policy === 'continue') return null;

          const rawInv = variant.inventory_quantity;
          if (rawInv === null || rawInv === undefined) return null;
          const invQty = parseInt(rawInv, 10);
          if (!Number.isFinite(invQty)) return null;

          const inCartQty =
            cart.items?.reduce((sum, item) => {
              const lineVariantId =
                item.variant_id != null ? Number(item.variant_id) : Number(item.id);
              return lineVariantId === variantId ? sum + item.quantity : sum;
            }, 0) || 0;

          const availableToAdd = Math.max(0, invQty - inCartQty);
          const partialQty = Math.min(requestedQty, availableToAdd);
          if (partialQty <= 0) return null;

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const partialData = new FormData(this.form);
          if (this.cart) {
            partialData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            partialData.append('sections_url', window.location.pathname);
          }
          partialData.set('quantity', String(partialQty));
          this.ensureColorCodeOnFormData(partialData, variantId);
          config.body = partialData;

          const partialRes = await fetch(`${routes.cart_add_url}`, config);
          const partialJson = await partialRes.json();
          const partialSt = Number(partialJson.status);
          if (Number.isFinite(partialSt) && partialSt >= 400) return null;

          return { ...partialJson, _partialQty: partialQty, _requestedQty: requestedQty };
        } catch (e) {
          console.error(e);
          return null;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
          else this.submitButtonText.textContent = window.variantStrings.addToCart;
          this.submitButton.classList.toggle('button--select-size', text === window.variantStrings.selectSize);
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.classList.remove('button--select-size');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      ensureColorCodeOnFormData(formData, variantId) {
        let colorCode = (formData.get('properties[_ColorCode]') || '').toString().trim();
        if (colorCode) return;

        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset?.section;
        if (sectionId && variantId) {
          const mapEl = document.getElementById(`VariantColorCodes-${sectionId}`);
          if (mapEl) {
            try {
              const map = JSON.parse(mapEl.textContent);
              colorCode = (map[String(variantId)] || '').toString().trim();
            } catch (e) {
              /* ignore */
            }
          }
        }

        if (!colorCode) {
          const colorInput = this.form.querySelector('input[name="properties[_ColorCode]"]');
          colorCode = (colorInput?.value || '').toString().trim();
        }

        if (colorCode) {
          formData.set('properties[_ColorCode]', colorCode);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
